/* Replace legacy useSendVerificationCode hook with this one after the refactor */

import { SignInIdentifier } from '@logto/schemas';
import { conditional } from '@silverhand/essentials';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendVerificationCodeApi } from '@/apis/utils';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import {
  UserFlow,
  type ContinueFlowInteractionEvent,
  type VerificationCodeIdentifier,
} from '@/types';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

const useSendVerificationCode = (flow: UserFlow, replaceCurrentPage?: boolean) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigate = useNavigate();

  const handleError = useErrorHandler();
  const asyncSendVerificationCode = useApi(sendVerificationCodeApi);
  const { setVerificationId, setIdentifierInputValue } = useContext(UserInteractionContext);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  type Payload = {
    identifier: VerificationCodeIdentifier;
    value: string;
    userId?: string;
    channel?: string;
  };

  const onSubmit = useCallback(
    async (
      { identifier, value, userId, channel }: Payload,
      interactionEvent?: ContinueFlowInteractionEvent
    ) => {
      const [error, result] = await asyncSendVerificationCode(
        flow,
        {
          type: identifier,
          value,
        },
        userId,
        interactionEvent,
        channel
      );

      if (error) {
        await handleError(error, {
          'guard.invalid_input': () => {
            setErrorMessage(
              identifier === SignInIdentifier.Email ? 'invalid_email' : 'invalid_phone'
            );
          },
        });

        return;
      }

      if (result) {
        // Store the verification ID in the context so that we can use it in the next step
        setVerificationId(codeVerificationTypeMap[identifier], result.verificationId);
        if (identifier === SignInIdentifier.Email) {
          setIdentifierInputValue({ type: SignInIdentifier.Email, value: result.email });
        } else {
          setIdentifierInputValue({ type: SignInIdentifier.Phone, value: result.phone });
        }
        // Preserve channel info in the URL so the verification-code page (and its
        // Resend button) can reuse it without needing extra context plumbing.
        const searchParams = new URLSearchParams(window.location.search);
        if (channel) {
          searchParams.set('channel', channel);
        } else {
          searchParams.delete('channel');
        }
        const newSearch = `?${searchParams.toString()}`;

        navigate(
          {
            pathname: `/${flow}/verification-code`,
            search: newSearch,
          },
          {
            replace: replaceCurrentPage,
            // Append the interaction event to the state so that we can use it in the next step
            ...conditional(
              flow === UserFlow.Continue && {
                state: { interactionEvent },
              }
            ),
          }
        );
      }
    },
    [
      asyncSendVerificationCode,
      flow,
      handleError,
      navigate,
      replaceCurrentPage,
      setIdentifierInputValue,
      setVerificationId,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useSendVerificationCode;
